<template>
  <LoadingContainer :is-loading="isLoading">
    <div class="card">
      <div class="card-header">
        <div class="card-header-title">
          <div class="title is-6 is-marginless">
            Card Link Lists
            <span class="tag ml-2">
              {{ lists.length }}
            </span>
          </div>
        </div>
        <div class="card-header-icon">
          <router-link
            :to="{ name: 'content.home.card-link-lists.add' }"
            class="button"
          >
            <span class="icon is-small">
              <i class="fa fa-plus" />
            </span>
            <span>Προσθήκη λίστας</span>
          </router-link>
        </div>
      </div>

      <div class="card-content">
        <BookshelfLinkNotice />
        <div class="columns">
          <div class="column is-full">
            <div class="table-wrapper">
              <table
                v-if="lists.length"
                class="table is-bordered is-striped is-marginless is-fullwidth"
              >
                <thead>
                  <tr>
                    <th class="has-text-centered" />
                    <th class="has-text-centered">#</th>
                    <th>Τίτλος (EL)</th>
                    <th>Τίτλος (EN)</th>
                    <th class="has-text-centered">Εμφανής</th>
                    <th class="has-text-centered">Ημ/νια Δημ.</th>
                    <th class="has-text-centered">Ημ/νια Ενημ.</th>
                    <th class="hidden-print">Ενέργειες</th>
                  </tr>
                </thead>

                <draggable
                  v-model="lists"
                  :move="canMove"
                  tag="tbody"
                  handle=".drag-handle"
                  @change="onReorder"
                >
                  <tr v-for="(row, idx) in lists" :key="row.id">
                    <!-- Drag -->
                    <td class="has-text-centered">
                      <button
                        v-tooltip="'Σύρετε για αναδιάταξη'"
                        class="button is-small is-white drag-handle"
                        type="button"
                      >
                        <span class="icon is-small">
                          <i class="fa fa-bars" />
                        </span>
                      </button>
                    </td>

                    <!-- Display index -->
                    <td class="has-text-centered">{{ idx + 1 }}</td>

                    <!-- Titles -->
                    <td>
                      <router-link
                        :to="{
                          name: 'content.home.card-link-lists.edit',
                          params: { id: row.id },
                        }"
                      >
                        {{ row.title_el }}
                      </router-link>
                    </td>
                    <td>{{ row.title_en }}</td>

                    <!-- Active toggle -->
                    <td class="has-text-centered">
                      <ToggleButton
                        :value="!!row['is_active']"
                        :sync="true"
                        :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                        :width="30"
                        :height="15"
                        @change="toggleActive(row, $event)"
                      />
                      <p class="help is-size-7 mt-1">Μέχρι 3 ενεργές λίστες</p>
                    </td>

                    <!-- Dates -->
                    <td class="has-text-centered">
                      {{ formatDateTime(row.created_at) }}
                    </td>
                    <td class="has-text-centered">
                      {{ formatDateTime(row.updated_at) }}
                    </td>

                    <!-- Actions -->
                    <td class="hidden-print actions-cell">
                      <div class="buttons are-small actions-buttons">
                        <router-link
                          :to="{
                            name: 'content.home.card-link-lists.items',
                            params: { id: row.id },
                          }"
                          class="button is-small is-link is-light"
                          title="Διαχείριση καρτών"
                        >
                          <span class="icon is-medium">
                            <i class="fa fa-th-large" />
                          </span>
                          <span class="has-text-weight-bold">Κάρτες</span>
                        </router-link>

                        <router-link
                          :to="{
                            name: 'content.home.card-link-lists.edit',
                            params: { id: row.id },
                          }"
                          class="button is-small"
                          title="Επεξεργασία λίστας"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-pencil" />
                          </span>
                        </router-link>

                        <button
                          class="button is-small is-outlined"
                          type="button"
                          title="Διαγραφή"
                          @click="askToDelete(row)"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-trash" />
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </draggable>
              </table>

              <h4 v-else class="title is-4 has-text-centered">
                Δε βρέθηκαν στοιχεία
              </h4>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete confirmation -->
      <Confirmation
        :model-name="selected ? selected.title_el : ''"
        :is-open="isConfirmOpen"
        description="Είστε βέβαιος για τη διαγραφή;"
        @closeModal="onConfirmClose"
      />
    </div>
  </LoadingContainer>
</template>

<script>
import BookshelfLinkNotice from '@/views/components/BookshelfLinkNotice';
import draggable from 'vuedraggable';
import request from '@/utils/request';
import LoadingContainer from '@/views/components/LoadingContainer';
import Confirmation from '@/views/components/Confirmation';

// If you use the same ToggleButton as the other page, it's probably globally registered.
// If not, import it here.

export default {
  name: 'CardLinkListsList',
  components: {
    BookshelfLinkNotice,
    draggable,
    LoadingContainer,
    Confirmation,
  },
  data: () => ({
    isLoading: false,
    lists: [],
    originalSnapshot: [],
    isConfirmOpen: false,
    selected: null,
  }),
  metaInfo: { title: 'Card Link Lists' },

  created() {
    this.fetch();
  },

  methods: {
    async fetch() {
      try {
        this.isLoading = true;
        const { data } = await request.get('/home/card-link-lists');

        // Normalize BE shape (pagination or array)
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && Array.isArray(data.data)) {
          list = data.data;
        }

        this.lists = list;
        this.snapshot();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.isLoading = false;
      }
    },

    snapshot() {
      this.originalSnapshot = JSON.parse(JSON.stringify(this.lists));
    },

    canMove(evt) {
      // For lists we allow free reordering
      const di = evt?.draggedContext?.index;
      const ri = evt?.relatedContext?.index;
      return typeof di === 'number' && typeof ri === 'number';
    },

    async onReorder() {
      // Reorder requires ANY existing ID in path; we can pick the first
      const pathId = this.lists[0]?.id;
      if (!pathId) return;

      const payload = this.lists.map((it, idx) => ({ id: it.id, order: idx }));

      try {
        await request.patch(`/home/card-link-lists/${pathId}/reorder`, {
          items: payload,
        });

        // reflect new order locally
        this.lists.forEach((it, idx) => {
          this.$set(it, 'order', idx);
        });

        this.snapshot();
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η αναδιάταξη ολοκληρώθηκε',
        });
      } catch (e) {
        // rollback UI
        this.lists = JSON.parse(JSON.stringify(this.originalSnapshot));
        const msg =
          e?.response?.data?.message || e?.message || 'To αίτημα απέτυχε';
        this.$notify({ type: 'error', title: 'Αποτυχία', text: msg });
      }
    },

    async toggleActive(row, evt) {
      const next = evt?.value !== undefined ? !!evt.value : !row.is_active;
      const prev = !!row.is_active;

      this.$set(row, 'is_active', next);

      try {
        await request.patch(`/home/card-link-lists/${row.id}`, {
          is_active: next,
        });

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: next
            ? 'Η λίστα είναι τώρα εμφανής.'
            : 'Η λίστα δεν είναι πλέον εμφανής.',
        });
      } catch (e) {
        // rollback on failure (e.g., > 3 active)
        this.$set(row, 'is_active', prev);

        const msg =
          e?.response?.data?.message || e?.message || 'To αίτημα απέτυχε';
        const uiMsg =
          msg.includes('up to 3') || msg.includes('Maximum of 3')
            ? 'Μέγιστο 3 ενεργές λίστες'
            : msg;

        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: uiMsg,
        });
      }
    },

    askToDelete(row) {
      this.selected = row;
      this.isConfirmOpen = true;
    },

    async onConfirmClose(confirmed) {
      this.isConfirmOpen = false;
      if (!confirmed || !this.selected) return;

      try {
        const { data } = await request.delete(
          `/home/card-link-lists/${this.selected.id}`,
        );
        if (data?.error) throw new Error(data.message || 'Server error');

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η λίστα διαγράφηκε',
        });

        await this.fetch();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.selected = null;
      }
    },

    formatDateTime(v) {
      if (!v) return '-';
      const s = String(v).trim();
      const parts = s.replace('T', ' ').split(' ');
      if (parts.length) {
        const [y, m, d] = (parts[0] || '').split('-');
        const timePart = (parts[1] || '').slice(0, 5);
        if (y && m && d) {
          const pad = n => String(n).padStart(2, '0');
          return `${pad(d)}-${pad(m)}-${y} ${timePart || '00:00'}`;
        }
      }
      const ts = Date.parse(s);
      if (!Number.isNaN(ts)) {
        const dt = new Date(ts);
        const pad = n => String(n).padStart(2, '0');
        return `${pad(dt.getDate())}-${pad(
          dt.getMonth() + 1,
        )}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      }
      return s;
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  position: relative;
}

.drag-handle {
  cursor: move;
}

.actions-cell {
  width: 180px;
  text-align: right;
  vertical-align: middle;
}

.actions-buttons {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.actions-buttons .button {
  margin: 0;
  min-width: 34px;
  justify-content: center;
  padding-left: 10px;
  padding-right: 10px;
}
</style>
